import { useCallback, useMemo, useRef, useEffect } from 'react';

/**
 * Hook personalizado para crear callbacks optimizados con throttling
 */
export const useOptimizedCallback = (callback, dependencies = [], delay = 0) => {
  const lastCallRef = useRef(0);
  
  return useCallback((...args) => {
    if (delay > 0) {
      const now = Date.now();
      if (now - lastCallRef.current < delay) {
        return;
      }
      lastCallRef.current = now;
    }
    return callback(...args);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callback, delay, ...dependencies]);
};

/**
 * Hook estable que mantiene la referencia del callback
 */
export const useStableCallback = (callback) => {
  const callbackRef = useRef(callback);
  
  useEffect(() => {
    callbackRef.current = callback;
  });
  
  return useCallback((...args) => {
    return callbackRef.current(...args);
  }, []); // Empty dependency array is intentional
};

/**
 * Hook para throttling de funciones
 */
export const useThrottle = (callback, delay) => {
  const lastCallRef = useRef(0);
  
  return useCallback((...args) => {
    const now = Date.now();
    if (now - lastCallRef.current >= delay) {
      lastCallRef.current = now;
      return callback(...args);
    }
  }, [callback, delay]);
};

/**
 * Hook para debouncing de funciones
 */
export const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);
  
  return useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
};

/**
 * Hook para memoizar valores pesados de calcular
 */
export const useHeavyMemo = (factory, dependencies, key = '') => {
  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && key) {
      console.log(`🔄 Recalculando ${key}`);
    }
    return factory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factory, key, ...dependencies]);
};

/**
 * Hook para crear referencias estables de funciones
 */
export const useEventCallback = (fn) => {
  const ref = useRef(fn);
  
  useEffect(() => {
    ref.current = fn;
  });
  
  return useCallback((...args) => ref.current(...args), []);
};
